// src/components/CommunitySharing.jsx
import {Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  FaInstagram,
  FaXTwitter,
  FaWhatsapp,
  FaEnvelope,
  FaLink,
  FaLinkedin,
  FaTelegram,
  FaFacebook,
} from "react-icons/fa6";
// SVGs for Signal, Discord, GroupMe
const indigo = '#818cf8'; // Tailwind indigo-400
const SignalIcon = (props) => (
  <svg viewBox="0 0 40 40" fill="none" width="1em" height="1em" {...props}>
    <circle cx="20" cy="20" r="18" fill={indigo} />
    <path d="M12 25c2 1.5 5.5 2.5 8 2.5s6-1 8-2.5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="20" cy="20" r="13" stroke="#fff" strokeWidth="2"/>
  </svg>
);
const DiscordIcon = (props) => (
  <svg viewBox="0 0 40 40" fill="none" width="1em" height="1em" {...props}>
    <circle cx="20" cy="20" r="18" fill={indigo} />
    <path d="M15 25c1.5 1 4.5 1 6 0" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
    <ellipse cx="16" cy="18" rx="1.5" ry="2" fill="#fff"/>
    <ellipse cx="24" cy="18" rx="1.5" ry="2" fill="#fff"/>
  </svg>
);
const GroupMeIcon = (props) => (
  <svg viewBox="0 0 40 40" fill="none" width="1em" height="1em" {...props}>
    <circle cx="20" cy="20" r="18" fill={indigo} />
    <rect x="12" y="15" width="16" height="10" rx="5" fill="#fff"/>
    <rect x="17" y="20" width="6" height="2" rx="1" fill={indigo}/>
  </svg>
);
import { handleShare } from '../MarketingUtils/Share';

const members = [
  { name: "Fatima", emoji: "??", quote: "The Oracle's wisdom gave me hope.", archetype: "Oracle", avatar: "https://randomuser.me/api/portraits/women/65.jpg", age: 54 },
  { name: "Miguel", emoji: "????", quote: "The Warrior taught me to stand tall.", archetype: "Warrior", avatar: "https://randomuser.me/api/portraits/men/75.jpg", age: 34 },
  { name: "Aisha", emoji: "????", quote: "The Healer helped me find peace.", archetype: "Healer", avatar: "https://randomuser.me/api/portraits/women/80.jpg", age: 28 },
  { name: "Hiroshi", emoji: "????", quote: "Rituals connect me to my ancestors.", archetype: "Dreamweaver", avatar: "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=facearea&w=256&h=256&facepad=2", age: 68 },
  { name: "Priya", emoji: "????", quote: "The Mystic inspires my dreams.", archetype: "Mystic", avatar: "https://randomuser.me/api/portraits/women/21.jpg", age: 16 },
  { name: "Omar", emoji: "????", quote: "Morning reflections guide my day.", archetype: "Seeker", avatar: "https://randomuser.me/api/portraits/men/19.jpg", age: 41 },
  { name: "Svetlana", emoji: "????", quote: "The Oracle's insight changed my life.", archetype: "Oracle", avatar: "https://randomuser.me/api/portraits/women/50.jpg", age: 37 },
  { name: "Peter", emoji: "????", quote: "The Warrior's courage is my strength.", archetype: "Warrior", avatar: "https://randomuser.me/api/portraits/men/81.jpg", age: 15 },
  { name: "Mary", emoji: "????", quote: "The Healer's kindness is a blessing.", archetype: "Healer", avatar: "https://randomuser.me/api/portraits/women/90.jpg", age: 72 },
  { name: "Adrian", emoji: "????", quote: "The Dreamweaver's rituals ground me.", archetype: "Dreamweaver", avatar: "https://randomuser.me/api/portraits/men/33.jpg", age: 29 },
  { name: "Zanele", emoji: "????", quote: "The Mystic's dreams inspire me.", archetype: "Mystic", avatar: "https://randomuser.me/api/portraits/women/43.jpg", age: 25 },
  { name: "Luca", emoji: "????", quote: "The Seeker's journey is my own.", archetype: "Seeker", avatar: "https://randomuser.me/api/portraits/men/14.jpg", age: 12 },
];

// Helper function to get color based on archetype
const getArchetypeColor = (archetype) => {
  const archetypeColors = {
    "Oracle": "#9333ea", // purple-600
    "Warrior": "#dc2626", // red-600
    "Healer": "#16a34a", // green-600
    "Dreamweaver": "#65a30d", // lime-600
    "Mystic": "#c026d3", // fuchsia-600
    "Seeker": "#2563eb", // blue-600
    // Default color for any other archetypes
    "Guide": "#4f46e5" // indigo-600
  };
  
  return archetypeColors[archetype] || archetypeColors.default;
};

export default function CommunitySharing() {

  const scrollRef = useRef(null);
  const [activePoint, setActivePoint] = useState(null);

  // Close popup when clicking outside
  useEffect(() => {
    if (!activePoint) return;
    const handleClick = (e) => {
      // Only close if click is outside any popup or profile image
      if (!e.target.closest('.profile-popup') && !e.target.closest('.profile-point')) {
        setActivePoint(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [activePoint]);

  // auto-scrolling member stories
  useEffect(() => {
    const scrollBox = scrollRef.current;
    let scrollAmount = 0;

    const scroll = () => {
      if (!scrollBox) return;
      scrollAmount += 0.3;
      if (scrollAmount >= scrollBox.scrollHeight / 2) {
        scrollAmount = 0;
      }
      scrollBox.scrollTop = scrollAmount;
      requestAnimationFrame(scroll);
    };

    scroll();
  }, []);

  // generate random constellation points tied to members
  // Fixed positions for each member (latitude/longitude mapped to % for demo)
  const memberPositions = [
    { top: '20%', left: '25%' }, // Fatima - USA
    { top: '15%', left: '50%' }, // Miguel - France
    { top: '30%', left: '60%' }, // Aisha - Middle East
    { top: '20%', left: '88%' }, // Hiroshi - East Asia
    { top: '32%', left: '71%' }, // Priya - India
    { top: '60%', left: '35%' }, // Omar - Brazil
    { top: '25%', left: '80%' }, // Svetlana - Russia
    { top: '10%', left: '20%' }, // Peter - Canada
    { top: '20%', left: '20%' }, // Maria - Europe
    { top: '12%', left: '54%' }, // Adrian - Germany
    { top: '55%', left: '60%' }, // Zanele - South Africa
    { top: '70%', left: '85%' }, // Luca - Australia
  ];

  return (
  <section className="relative w-full max-w-6xl mx-auto px-6 py-16 text-center">
     
      {/* Members experience box with Global Map */}
      <div className="relative mb-16">
        <div className="relative w-full h-100 bg-indigo-900 rounded-2xl shadow-2xl overflow-hidden">
          {/* World map background image */}
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/83/Equirectangular_projection_SW.jpg/1200px-Equirectangular_projection_SW.jpg"
            alt="World Map"
            className="absolute inset-0 w-full h-full object-cover opacity-80 z-0 pointer-events-none select-none"
            style={{ filter: 'contrast(1.5) brightness(1)' }}
          />
          {/* gradient layer */}
          <div className="absolute inset-0 bg-linear-to-b from-indigo-900/70 to-indigo-950" />


          
          {/* Constellation points with profile images */}
          {/* Member profile points over the map */}
          {members.map((m, i) => (
            <div
              key={i}
              className="absolute flex items-center justify-center cursor-pointer profile-point"
              style={{
                top: memberPositions[i]?.top || '50%',
                left: memberPositions[i]?.left || '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 2
              }}
              onClick={() => setActivePoint({ ...m, top: memberPositions[i]?.top || '50%', left: memberPositions[i]?.left || '50%' })}
            >
              {/* Glowing ring around profile */}
              <div className="absolute w-10 h-10 rounded-full animate-pulse bg-indigo-500 opacity-20"></div>
              {/* Profile image */}
              <div className="relative w-8 h-8 rounded-full overflow-hidden border-2 border-indigo-400 shadow-lg hover:scale-125 transition-transform duration-200">
                <img
                  src={m.avatar}
                  alt={m.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Small dot indicator for archetype */}
              <div
                className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border border-indigo-200"
                style={{ backgroundColor: getArchetypeColor(m.archetype) }}
              ></div>
            </div>
          ))}

          {/* Tooltip for active point */}
          {activePoint && (
            <div
              className="absolute bg-indigo-50 backdrop-blur-sm text-indigo-950 px-4 py-3 rounded-xl shadow-xl text-left w-64 border border-indigo-300 animate-fadeIn z-10 profile-popup"
              style={{
                top: activePoint.top,
                left: activePoint.left,
                transform: "translate(-100%, -30%)",
              }}
            >
              <div className="flex items-start space-x-3">
                <img 
                  src={activePoint.avatar} 
                  alt={activePoint.name} 
                  className="w-16 h-16 rounded-full object-cover border-2 border-indigo-400 shrink-0" 
                />
                <div className="grow min-w-0">
                  <p className="font-semibold text-indigo-900 text-base flex items-center gap-2">
                    {activePoint.emoji} {activePoint.name}
                  </p>
                  <div 
                    className="inline-block mt-1 px-3 py-1 text-sm rounded-full"
                    style={{ 
                      backgroundColor: `${getArchetypeColor(activePoint.archetype)}20`,
                      color: getArchetypeColor(activePoint.archetype)
                    }}
                  >
                    {activePoint.archetype}
                  </div>
                  <p className="text-sm text-indigo-700 mt-2 italic">"{activePoint.quote}"</p>
                </div>
              </div>
              <div className="flex justify-end mt-2">
                <button
                  className="text-xs px-3 py-1 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePoint(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          <p className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-indigo-200 text-lg italic">
            "You are not alone. The sanctuary lives in many hearts."
          </p>
        </div>
      </div>

      {/*  Sharing Interface */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold text-indigo-100 mb-4">
          Invite Someone to Join
        </h2>
        <p className="text-indigo-200 text-base mb-6">
          Your presence here is already a gift. By sharing Andlegur, you open the door for others to find clarity, alignment, and purpose. 
          Every invitation you extend builds a stronger circle of seekers. Whether it's family, friends, or community, your voice carries the light forward.
        </p>
  <div className="flex flex-wrap justify-center gap-6 text-indigo-300 mb-6 text-2xl">
          <span
            className="text-indigo-400 hover:text-blue-400 hover:scale-110 transition-transform cursor-pointer text-2xl"
            title="Share on Signal"
            onClick={() => window.open(`https://signal.me/#p/${encodeURIComponent(window.location.href)}`, '_blank')}
            style={{ lineHeight: 0 }}
          >
            <SignalIcon className="w-7 h-7 align-middle" />
          </span>
          <span
            className="text-indigo-400 hover:text-indigo-500 hover:scale-110 transition-transform cursor-pointer text-2xl"
            title="Share on Discord"
            onClick={() => window.open(`https://discord.com/channels/@me`, '_blank')}
            style={{ lineHeight: 0 }}
          >
            <DiscordIcon className="w-7 h-7 align-middle" />
          </span>
          <span
            className="text-indigo-400 hover:text-cyan-400 hover:scale-110 transition-transform cursor-pointer text-2xl"
            title="Share on GroupMe"
            onClick={() => window.open(`https://groupme.com/share?text=${encodeURIComponent('Discover Andlegur � the sanctuary for healing, purpose, and awakening. ' + window.location.href)}`, '_blank')}
            style={{ lineHeight: 0 }}
          >
            <GroupMeIcon className="w-7 h-7 align-middle" />
          </span>
          <FaLinkedin
            className="hover:text-blue-500 hover:scale-110 transition-transform cursor-pointer"
            onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
            title="Share on LinkedIn"
          />
          <FaTelegram
            className="hover:text-sky-400 hover:scale-110 transition-transform cursor-pointer"
            onClick={() => window.open(`https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent('Discover Andlegur � the sanctuary for healing, purpose, and awakening.')}`, '_blank')}
            title="Share on Telegram"
          />
          <FaFacebook
            className="hover:text-blue-600 hover:scale-110 transition-transform cursor-pointer"
            onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
            title="Share on Facebook"
          />
          <FaInstagram
            className="hover:text-pink-400 hover:scale-110 transition-transform cursor-pointer"
            onClick={() => window.open(`https://www.instagram.com/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
            title="Share on Instagram"
          />
          <FaXTwitter
            className="hover:text-sky-400 hover:scale-110 transition-transform cursor-pointer"
            onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent('Discover Andlegur � the sanctuary for healing, purpose, and awakening. ' + window.location.href)}`, '_blank')}
            title="Share on X (Twitter)"
          />
          <FaWhatsapp
            className="hover:text-green-400 hover:scale-110 transition-transform cursor-pointer"
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent('Discover Andlegur � the sanctuary for healing, purpose, and awakening. ' + window.location.href)}`, '_blank')}
            title="Share on WhatsApp"
          />
          <FaEnvelope
            className="hover:text-indigo-600 hover:scale-110 transition-transform cursor-pointer"
            onClick={() => window.open(`mailto:?subject=Andlegur Sanctuary&body=${encodeURIComponent('Discover Andlegur � the sanctuary for healing, purpose, and awakening. ' + window.location.href)}`)}
            title="Share via Email"
          />
          <FaLink
            className="hover:text-indigo-100 hover:scale-110 transition-transform cursor-pointer"
            onClick={handleShare}
            title="Copy Link or Share"
          />
        </div>
      </div>

  
      {/*  Call to Action */}
      <div className="mt-12">
        <p className="text-indigo-200 italic mb-6 text-lg">
          "Discover not just a page, but a space of belonging. The sanctuary
          awaits your presence."
        </p>
        <Link className="px-8 py-3 bg-indigo-600 text-white font-semibold rounded-full shadow-lg hover:bg-indigo-500 transition"
          to="/sanctuary"
        >
          Discover the Space
        </Link>
      </div>
    </section>
  );
}
